import { Box, FormControlLabel, MenuItem, Tooltip } from "@mui/material";
import React, { useMemo } from "react";
import RadioMenuItem from "./RadioMenuItem";
import { getDisplayName } from "../utils/strings";

type PatientGroupMenuItem = {
  groupKey: string;
  subGroups?: string[];
};

type PatientGroupSelectorProps = {
  rolesPatientGroup: PatientGroupMenuItem[];
  patientGroup?: { [key: string]: string };
  patientSubGroup?: { [key: string]: string };
  userEmail: string;
  onPatientGroupClick: (group: string, subGroup?: string) => void;
};

const PatientGroupSelector = ({
  rolesPatientGroup,
  patientGroup,
  patientSubGroup,
  userEmail,
  onPatientGroupClick,
}: PatientGroupSelectorProps) => {
  const sortedGroups = useMemo(() => {
    return [...rolesPatientGroup].sort((a, b) =>
      a.groupKey.localeCompare(b.groupKey)
    );
  }, [rolesPatientGroup]);
  const currentGroup = patientGroup?.[userEmail];
  const currentSubGroup = patientSubGroup?.[userEmail];

  return (
    <>
      {sortedGroups.map(({ groupKey, subGroups }) => {
        const hasSubGroups = !!subGroups?.length;
        const isCurrentGroup = currentGroup === groupKey;
        
        // Check if a subgroup is selected for THIS group
        const hasSubGroupForThisGroup =
          isCurrentGroup &&
          !!currentSubGroup &&
          currentSubGroup.trim() !== "" &&
          subGroups?.includes(currentSubGroup);
        
        // Group is selected if: it's the current group AND no subgroup is selected for this group
        const isGroupSelected = isCurrentGroup && !hasSubGroupForThisGroup;
        
        return (
          <React.Fragment key={groupKey}>
            {hasSubGroups ? (
              <>
                <MenuItem
                  disabled
                  sx={{
                    paddingX: "35px",
                    cursor: "default",
                  }}
                >
                  <FormControlLabel
                    label={getDisplayName(groupKey)}
                    disabled
                    control={<Box sx={{ width: 24, height: 24 }} />}
                  />
                </MenuItem>
                <div style={{ paddingLeft: 35 }}>
                  {subGroups
                    .slice()
                    .sort((a, b) => a.localeCompare(b))
                    .map((subGroup: string) => {
                      const isSubGroupSelected =
                        isCurrentGroup && currentSubGroup === subGroup;
                      return (
                        <Tooltip title={subGroup} placement="bottom">
                          <div>
                            <RadioMenuItem
                              key={subGroup}
                              value={subGroup}
                              label={getDisplayName(subGroup)}
                              selectedValue={
                                isSubGroupSelected ? subGroup : undefined
                              }
                              onChange={() =>
                                onPatientGroupClick(groupKey, subGroup)
                              }
                            />
                          </div>
                        </Tooltip>
                      );
                    })}
                </div>
              </>
            ) : (
              <Tooltip title={groupKey} placement="bottom">
                <div>
                  <RadioMenuItem
                    value={groupKey}
                    label={getDisplayName(groupKey)}
                    selectedValue={isGroupSelected ? groupKey : undefined}
                    onChange={() => onPatientGroupClick(groupKey)}
                  />
                </div>
              </Tooltip>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default PatientGroupSelector;
